import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LoadingPage } from "@/components/ui/loading-page";
import { useAuth } from "@/contexts/auth-context";
import { useClientProfile } from "@/contexts/client-profile-context";
import { usePermissions } from "@/contexts/permission-context";
import { useOnboardingStatus } from "@/hooks/hr/training/use-trainings";

interface OnboardingGateProps {
  children: ReactNode;
}

/**
 * Casca vazia para quem não tem formações obrigatórias.
 *
 * Dois casos, e os dois pelo mesmo motivo — o pedido a `/trainings` não pode
 * sequer ser feito:
 *
 * 1. **Contas de posto de segurança.** Um posto de portaria não é uma pessoa da
 *    empresa; o backend responde 403 a `/trainings`.
 * 2. **Instalações sem o módulo de RH.** A rota não existe para elas: o gate de
 *    módulo responde **404**, e a página inteira ficava a repetir o pedido numa
 *    consola cheia de erros.
 *
 * Existe como componente separado, e não como `if` dentro do gate, porque os
 * hooks têm de correr sempre — `useOnboardingStatus` não pode ser condicional.
 */
function SemFormacoesObrigatorias({ children }: OnboardingGateProps) {
  return <>{children}</>;
}

/**
 * OnboardingGate - Route guard for mandatory onboarding system
 *
 * Blocks platform access for users with incomplete onboarding trainings.
 * Redirects to /dashboard/onboarding if requirements are not met.
 *
 * Exemptions:
 * - Admin users (superuser or Admin department) always bypass
 * - Users on /dashboard/onboarding page (prevent redirect loop)
 * - Users on /login or /access-denied pages
 * - Users with no required trainings (is_complete = true)
 *
 * Error handling:
 * - Network errors: Show error with retry, don't block access
 * - Loading state: Show full-screen spinner
 */
export function OnboardingGate({ children }: OnboardingGateProps) {
  const { user } = useAuth();
  const { hasModule } = useClientProfile();

  // Sem o módulo de RH, `/trainings` não existe nesta instalação (404). Com
  // conta de posto, existe mas é proibida (403). Nos dois casos o pedido não
  // se faz — ver `SemFormacoesObrigatorias`.
  if (!hasModule("hr") || user?.isSecurityAccount) {
    return <SemFormacoesObrigatorias>{children}</SemFormacoesObrigatorias>;
  }

  return <OnboardingGateInner>{children}</OnboardingGateInner>;
}

function OnboardingGateInner({ children }: OnboardingGateProps) {
  const location = useLocation();
  const { isAdmin } = usePermissions();
  const { data: onboardingStatus, isLoading, error } = useOnboardingStatus();

  // Quem é administrador nunca é barrado (mais abaixo): esperar pela resposta só atrasava a entrada. O pedido continua a
  // correr em segundo plano para a cache já vir quente. Os outros esperam no mesmo ecrã de espera, sem texto novo.
  if (isLoading && !isAdmin) {
    return <LoadingPage />;
  }

  // Network error - don't block access, just log error
  if (error) {
    console.error("Failed to fetch onboarding status:", error);
    // Allow access on error to prevent blocking users
    return <>{children}</>;
  }

  // Admin bypass - admins always have access
  if (isAdmin) {
    return <>{children}</>;
  }

  // Check if current path should bypass the gate
  // Allow: onboarding page, login, access-denied, and training player (so user can DO the training)
  const bypassPaths = ["/dashboard/onboarding", "/login", "/access-denied"];
  const shouldBypass = bypassPaths.some((path) =>
    location.pathname.startsWith(path)
  ) || /\/dashboard\/hr\/trainings\/[^/]+\/player/.test(location.pathname);

  if (shouldBypass) {
    return <>{children}</>;
  }

  // Check onboarding completion status
  if (onboardingStatus && !onboardingStatus.is_complete) {
    // Redirect to onboarding page
    return <Navigate to="/dashboard/onboarding" replace />;
  }

  // Onboarding complete or no requirements - allow access
  return <>{children}</>;
}
